import React, { useCallback, useEffect, useState } from "react";
import DOMPurify from "dompurify";
import { FiEdit2, FiPlus, FiTrash2 } from "react-icons/fi";
import { toast } from "react-toastify";
import CircularLoader from "components/common/CircularLoader/CircularLoader";
import { PaConfirmModal } from "components/platform-admin";
import { deleteFeedEvent, fetchFeedEvents } from "./feedEvents.api";
import EventFormModal from "./EventFormModal";
import { eventImageSrc, feedEventErrorMessage } from "./feedEvents.utils";
import "./FeedEvents.css";

const EventsManager = ({
  canManage = false,
  showEmpty = true,
  adminList = false,
}) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadEvents = useCallback(async () => {
    setLoading(true);

    try {
      const nextEvents = await fetchFeedEvents({ admin: adminList });
      setEvents(Array.isArray(nextEvents) ? nextEvents : []);
    } catch (err) {
      setEvents([]);
      console.log(err);
    } finally {
      setLoading(false);
    }
  }, [adminList]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const handleDelete = async () => {
    if (!deleteTarget?.uuid) {
      return;
    }

    setDeleting(true);

    try {
      await deleteFeedEvent(deleteTarget.uuid);
      toast.success("Event deleted");
      setDeleteTarget(null);
      await loadEvents();
    } catch (err) {
      toast.error(feedEventErrorMessage(err, "Failed to delete event"));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="feed-events">
      {canManage ? (
        <div className="mb-3 flex justify-end">
          <button
            type="button"
            onClick={() => {
              setEditing(null);
              setFormOpen(true);
            }}
            className="inline-flex items-center gap-1 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            <FiPlus className="h-4 w-4" aria-hidden="true" />
            Add event
          </button>
        </div>
      ) : null}

      {loading ? (
        <div className="flex w-full items-center justify-center py-6">
          <CircularLoader
            className="flex items-center justify-center"
            classloader="loader-l"
          />
        </div>
      ) : events.length > 0 ? (
        <div className="feed-events-list">
          {events.map((event) => {
            const imageSrc = eventImageSrc(event.image);

            return (
              <article key={event.uuid || event.id} className="feed-event-card">
                {imageSrc ? (
                  <img src={imageSrc} alt="" className="feed-event-card__media" />
                ) : null}
                <div className="feed-event-card__body">
                  <div className="feed-event-card__header">
                    <h3 className="feed-event-card__title feed-event-card__title--plain">
                      {event.title || "Untitled event"}
                    </h3>
                    {canManage ? (
                      <div className="feed-event-card__actions">
                        <button
                          type="button"
                          aria-label={`Edit ${event.title || "event"}`}
                          onClick={() => {
                            setEditing(event);
                            setFormOpen(true);
                          }}
                          className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-blue-600"
                        >
                          <FiEdit2 className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          aria-label={`Delete ${event.title || "event"}`}
                          onClick={() => setDeleteTarget(event)}
                          className="rounded-md p-1.5 text-gray-500 hover:bg-red-50 hover:text-red-600"
                        >
                          <FiTrash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ) : null}
                  </div>
                  <div
                    className="feed-event-card__text"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(event.description || "", {
                        ALLOWED_ATTR: ["target", "href", "rel"],
                      }),
                    }}
                  />
                </div>
              </article>
            );
          })}
        </div>
      ) : showEmpty ? (
        <p className="mb-2 text-center text-sm font-semibold">
          No events available
        </p>
      ) : canManage ? (
        <p className="text-center text-sm text-gray-500">
          Posted events appear here for every organization.
        </p>
      ) : null}

      <EventFormModal
        open={formOpen}
        event={editing}
        onClose={() => setFormOpen(false)}
        onSaved={loadEvents}
      />

      <PaConfirmModal
        open={Boolean(deleteTarget)}
        onClose={() => {
          if (!deleting) {
            setDeleteTarget(null);
          }
        }}
        title="Delete event"
        description={`Delete "${deleteTarget?.title || "this event"}"? It will be removed for every organization.`}
        confirmLabel="Delete"
        destructive
        submitting={deleting}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default EventsManager;
